import { useParameterStore } from "@/frontend/store/ParameterStore";
import { I18n } from "@/i18n/I18n";
import { CUSTOM_FIELD_SUBJECT_TYPES, CUSTOM_FIELD_TYPES, ObjectKeyVisibility } from "@/types/customFieldTypes";
import { ParameterInputType } from "@/types/parameterTypes";
import { Parameter } from "@/backend/models/parameter";
import { useState } from "react";
import { Form, Offcanvas } from "react-bootstrap";
import { useShallow } from "zustand/react/shallow";
import { CollapseButton } from "../buttons/CollapseButton";
import { CustomFieldDropdown } from "../customFIelds/CustomFieldDropdown";
import { AddValueToParameterButtons } from "./AddValueToParameterButtons";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { Plus, X } from "lucide-react";
import { ObjectListEdit } from "../objectList/ObjectListEdit";
import { NeutralDeleteButton } from "../buttons/NeutralDeleteButton";
import { ObjectKeyInput } from "../customFIelds/customFieldObjects/ObjectKeyInput";
import { Select } from "../Select";
import { ObjectKeyProperty } from "@/backend/models/objectInstance";
import { ButtonParent } from "../buttons/ButtonParent";

export function NonPrimitiveParameterModal(props: { parameterId: string, setShowEditValues: (show: boolean) => void, runView: boolean }) {
  const { parameterId, setShowEditValues, runView } = props;
  const parameter = useParameterStore(useShallow((state) => state.parameters.find((p) => p.id === parameterId)));

  if (!parameter) {
    return null;
  }

  return (
    <Offcanvas
      show
      placement="end"
      className="side-panel"
    >
      <Offcanvas.Body className='side-panel-container d-flex flex-column'>
        <div>
          <button
            type="button"
            className="btn btn-sm btn-blue-light"
            onClick={() => setShowEditValues(false)}
            aria-label={I18n.get('close')}
          >
            <X size={20} />
          </button>
          <h4 className="panel-heading my-4">{I18n.get('editItem', { title: parameter.title })}</h4>
        </div>
      <ModalContent parameter={parameter} runView={runView} />
      </Offcanvas.Body>
    </Offcanvas>
  );
}

export function ModalContent(props: { parameter: Parameter, runView: boolean }) {
  const { parameter, runView } = props;
  const step = useTreeStore(useShallow(state => state.containerLookup[parameter?.subjectId || '']));
  const onlyPropertiesShouldBeEdited = parameter.inputType === ParameterInputType.TABLE || (step?.container?.isRepeatingStep() && parameter.fieldType === CUSTOM_FIELD_TYPES.Object && parameter.inputType === ParameterInputType.LIST);

  if (onlyPropertiesShouldBeEdited) {
    return <ShapeEdit parameter={parameter} />;
  }

  return (
    <>
      <div className='flex-grow-1 overflow-auto mb-3'>
        { !runView && <ObjectShapeEdit parameter={parameter} /> }
        <ObjectListEdit parameter={parameter} runView={runView}/>
      </div>
      <ModalFooter parameter={parameter} runView={runView} />
    </>
  );
}

export function ModalFooter(props: { parameter: Parameter, runView: boolean }) {
  const { parameter, runView } = props;
  const createCustomField = useParameterStore(state => state.createCustomField);

  if (parameter.inputType === ParameterInputType.FREE_FORM) {
    return null;
  }

  const choiceOnRunView = runView && parameter.isChoice();
  if (choiceOnRunView) {
    if (parameter.allowCustomChoices) {
      const onAddValue = () => {
        void createCustomField({
          subjectId: parameter.id,
          subjectType: CUSTOM_FIELD_SUBJECT_TYPES.PARAMETER,
          fieldType: parameter.fieldType,
          isSelected: true,
          objectInstance:  {
            objectShapeId: parameter.objectShapeId,
            title: `${parameter.title} ${parameter.fields.length + 1}`,
          },
          isCustomChoice: true,
        });
      };

      return <div className='side-panel-footer'>
        <ButtonParent className='fusa-btn-small-with-icon fusa-btn-primary' title={I18n.get('addCustomValue')} onClick={onAddValue}>
          <Plus size={16} />
          <span>{I18n.get('addCustomValue')}</span>
        </ButtonParent>
      </div>;
    }

    return null;
  }

  return <div className='side-panel-footer'><AddValueToParameterButtons parameter={parameter} /></div>;
}

export function ObjectShapeEdit(props: { parameter: Parameter}) {
  const { parameter } = props;
  const [showProperties, setShowProperties] = useState(parameter.propertyKeys.length === 0);

  return (
    <div className='card mb-2'>
      <div className='d-flex align-items-center card-header'>
        <CollapseButton
          showElement={showProperties}
          setShowElement={setShowProperties}
          title={I18n.get('properties')}
        />
        <div>{I18n.get('properties')}</div>
      </div>
      { showProperties && <div className='card-body'>
        <ShapeEdit parameter={parameter} />
      </div> }
    </div>
  );
}

function ShapeEdit(props: { parameter: Parameter }) {
  const { parameter } = props;
  const handleUpdatedConsumers = useParameterStore(state => state.handleUpdatedParamsAndRefs);
  const addObjectKey = useParameterStore(state => state.addObjectKey);
  const { objectShapeId } = parameter;

  if (!objectShapeId) return null;

  const visibility = parameter.isChoice() ? ObjectKeyVisibility.HIDDEN : ObjectKeyVisibility.EDITABLE;

  return (
    <div>
      <div className='d-flex flex-column gap-3'>
        {parameter.propertyKeys.map(property => <KeyEdit key={property.keyId} property={property} parameter={parameter} />)}
      </div>
      <CustomFieldDropdown onClick={(type) => void addObjectKey({ objectShapeId, type, visibility }, handleUpdatedConsumers)} label={parameter.inputType === ParameterInputType.TABLE ? I18n.get('addColumn') : I18n.get('addProperty')} includeFile />
    </div>
  );
}

function KeyEdit(props: { property: ObjectKeyProperty, parameter: Parameter }) {
  const { property, parameter } = props;
  const handleUpdatedConsumers = useParameterStore(state => state.handleUpdatedParamsAndRefs);
  const updateObjectKey = useParameterStore(state => state.updateObjectKey);
  const deleteObjectKey = useParameterStore(state => state.deleteObjectKey);
  const [title, setTitle] = useState(property.title);
  const [description, setDescription] = useState(property.description);
  const keyLabel = parameter.inputType === ParameterInputType.TABLE ? I18n.get('column') : I18n.get('identifier');

  return (
    <div>
      <div className="d-flex gap-2">
        <ObjectKeyInput property={property} properties={parameter.propertyKeys} handleUpdatedConsumers={handleUpdatedConsumers} keyLabel={keyLabel} />
        <div>
          <Form.Label className="form-label-neutral">{I18n.get('visibility')}</Form.Label>
          <Select value={I18n.get(property.visibility)} label={I18n.get('visibility')} minHeight='48px'>
            { Object.values(ObjectKeyVisibility).map((inputFieldType) => (
              <button type="button" key={inputFieldType} className="w-100 dropdown-item" onClick={() => void updateObjectKey({ ...property, id: property.keyId, name: property.key, visibility: inputFieldType }, handleUpdatedConsumers)}>
                {I18n.get(inputFieldType)}
              </button>
              ))}
          </Select>
        </div>
        <div className='d-flex align-items-end'><NeutralDeleteButton onSubmit={() => deleteObjectKey(property.keyId, handleUpdatedConsumers)} itemType={I18n.get('property')} title='' /></div>
      </div>
      <div>
          <Form.Label className="form-label-neutral">{I18n.get('title')}</Form.Label>
          <Form.Control
            className="form-control-neutral"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => void updateObjectKey({ ...property, id: property.keyId, name: property.key, title }, handleUpdatedConsumers)} />
        </div>
        <div>
          <Form.Label className="form-label-neutral">{I18n.get('description')}</Form.Label>
          <Form.Control
            as="textarea"
            className="form-textarea-neutral"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => void updateObjectKey({ ...property, id: property.keyId, name: property.key, description }, handleUpdatedConsumers)} />
        </div>
        <hr />
    </div>
  );
}
